import { createServerFn } from '@tanstack/react-start';
import z from 'zod';

import { logger } from '~ai-tutor/lib/logger/logger';

import { useAppSession } from '../../auth/session';
import { type AiTutorAccessTokenDdb, aiTutorSettingsRepository } from '../../ddb/aiTutorSettings';
import { sessionRepository } from '../../ddb/sessions';
import { tokenRequiresOidcLogin } from './utils';

const loginRequestBodySchema = z.object({
  token: z.string().min(1, 'Token is required'),
});
type LoginRequestBody = z.infer<typeof loginRequestBodySchema>;

const validateToken = async (token: string): Promise<AiTutorAccessTokenDdb | undefined> => {
  const repo = aiTutorSettingsRepository();
  const accessToken = await repo.getAccessToken(token);
  return accessToken?.enabled ? accessToken : undefined;
};

const storeAuthSession = async (sessionId: string, token: string, eventId: string) => {
  const sessionRepo = sessionRepository();
  await sessionRepo.storeSession(sessionId, { token, eventId });
};

const loginWithToken = async ({ token }: LoginRequestBody) => {
  logger.info('Login attempt with token');
  const accessToken = await validateToken(token);

  if (!accessToken) {
    logger.info('Login attempt with token failed: Invalid credentials');
    return { success: false, error: 'Invalid credentials' };
  }

  if (tokenRequiresOidcLogin(accessToken)) {
    logger.info('Login attempt with token failed: Token requires OIDC login');
    return {
      success: false,
      tokenRequiresOidcLogin: true,
      error: 'Please login with your account to use this token',
    };
  }

  const session = await useAppSession();

  if (!session.id) {
    throw new Error('Session ID is undefined');
  }

  await storeAuthSession(session.id, accessToken.token, accessToken.parentId);
  logger.info('Login attempt with token succeeded');

  await session.update({
    authenticated: true,
  });

  return { success: true };
};

export const tokenRequiresOidcLoginFn = createServerFn({ method: 'POST' })
  .validator(loginRequestBodySchema)
  .handler(async ({ data }) => {
    const accessToken = await validateToken(data.token);
    return accessToken ? tokenRequiresOidcLogin(accessToken) : false;
  });

export const loginFn = createServerFn({ method: 'POST' })
  .validator(loginRequestBodySchema)
  .handler(({ data }) => loginWithToken(data));
